import { dissoc } from 'ramda'
import type { ToRefs, Ref, UnwrapRef } from 'vue'
import type { SearchParameters } from 'ofetch'
import type { IndexSearchListing } from '@/entities/search'

export type UseIndexSearch<T, U> = ToRefs<U> & {
  loading: Readonly<Ref<boolean>>
  error: Readonly<Ref<Error | null>>
  results: Readonly<Ref<readonly T[]>>
  count: Readonly<Ref<number>>
  maxPage: Readonly<Ref<number>>
  page: Ref<number>
  continuation: Ref<number>
  refresh: () => void
}

interface State<T> {
  loading: boolean
  error: Error | null
  results: T[]
  count: number
}

export interface QueryParameters {
  query: string | null
  limit: number
}

interface IndexSearchSetup<Q extends QueryParameters, R> {
  initParams: Partial<Q> & { page?: number }
  endpoint(params: Q): string | undefined
  finalParameters(params: Q): R
}

export default function useIndexSearch<T, U extends QueryParameters, V extends SearchParameters>(
  setup: IndexSearchSetup<U, V>
): UseIndexSearch<T, U> {
  const state = reactive<State<T>>({
    loading: false,
    error: null,
    results: [],
    count: 0,
  })

  let abortController: AbortController | null

  const page = ref(setup.initParams.page || 0)
  const continuation = ref(0)

  const frame = computed(() => page.value || continuation.value)
  const maxPage = computed(() => Math.ceil(state.count / parameters.limit))

  const parameters = reactive<QueryParameters>(Object.assign({ query: '', limit: 10 }, dissoc('page', setup.initParams))) as U

  const endpoint = computed(() => setup.endpoint(parameters))

  const finalParameters = computed(() => {
    return {
      limit: parameters.limit,
      offset: parameters.limit * frame.value,
      ...setup.finalParameters(parameters as U),
    } as V
  })

  watch(
    () => ({ ...parameters }),
    () => {
      state.count = 0
      state.results = []
      page.value = 0
      continuation.value = 0
    }
  )

  watch(
    () => ({ ...finalParameters.value, endpoint: endpoint.value }),
    () => _fetchResults().then(_handleData),
    { immediate: true }
  )

  watch(
    () => [continuation.value, page.value],
    () => {
      if (continuation.value > 0 && page.value > 0) {
        state.error = new Error('Only page or continuation can be controlled at one time.')
      } else {
        state.error = null
      }
    }
  )

  function refresh() {
    continuation.value = 0
    page.value = 0
    _fetchResults().then(_handleData)
  }

  async function _fetchResults() {
    if (!endpoint.value) return

    try {
      state.loading = true

      const data = await _getData(endpoint.value, finalParameters.value)

      if (Array.isArray(data)) {
        state.count = data.length
      } else {
        state.count = data.count
      }

      return data.results || data
    } catch (e) {
      if (!(e instanceof AbortController) && e instanceof Error) {
        state.error = e
      }
    } finally {
      state.loading = false
    }
  }

  async function _getData(endpoint: string, params: V) {
    if (abortController) {
      abortController.abort()
    }

    abortController = new AbortController()
    const data = await $fetch<IndexSearchListing<T>>(endpoint, {
      ...useFetchSharedOptions(),
      params,
      signal: abortController.signal,
    })

    return data
  }

  function _handleData(results?: T[]) {
    if (!results) return

    if (continuation.value > 0) {
      state.results = state.results.concat(results as UnwrapRef<T[]>)
    } else {
      state.results = results as UnwrapRef<T[]>
    }
  }

  // TODO: Fix tsc issue
  return { ...toRefs(readonly(state)), ...toRefs(parameters), maxPage, page, continuation, refresh } as any
}
